import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress
} from '@mui/material';
import { Mic as MicIcon } from '@mui/icons-material';
import { DirectAudioRecorder } from 'components/Audio/DirectAudioRecorder';

interface VoiceNoteEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (name: string, blob: Blob, duration: number) => void;
}

export const VoiceNoteEditDialog: React.FC<VoiceNoteEditDialogProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [name, setName] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!audioBlob || !name.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit(name.trim(), audioBlob, audioDuration);
      setName('');
      setAudioBlob(null);
      setAudioDuration(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecordingComplete = (blob: Blob, duration: number) => {
    setAudioBlob(blob);
              setAudioDuration(duration);
  };

  const handleDialogClose = () => {
    setName('');
    setAudioBlob(null);
    setAudioDuration(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 1,
          border: '1px solid rgb(229, 231, 235)'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 2,
        pb: 1.5,
        fontSize: '1.125rem',
        fontWeight: 600,
        color: 'rgb(17, 24, 39)',
        borderBottom: '1px solid rgb(229, 231, 235)'
      }}>
        New Voice Note
      </DialogTitle>
      <DialogContent sx={{ p: 2, pt: 2, mt: 2.5, overflow: 'visible' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Voice Note Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined" 
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                fontSize: '0.875rem',
                '& fieldset': {
                  borderColor: 'rgb(229, 231, 235)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgb(107, 114, 128)'
                }
              }
            }}
          />
          
          <Box sx={{ 
            border: '1px solid rgb(229, 231, 235)', 
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            <DirectAudioRecorder 
              onAccept={handleRecordingComplete}
              onCancel={handleDialogClose}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={handleDialogClose}
          variant="outlined"
          sx={{
            color: 'rgb(75, 85, 99)',
            textTransform: 'none',
            borderColor: 'rgb(229, 231, 235)',
            fontWeight: 500,
            '&:hover': {
              borderColor: 'rgb(107, 114, 128)',
              backgroundColor: 'transparent'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || !audioBlob || isSubmitting}
          sx={{
            bgcolor: '#0a66c2',
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1,
            '&:hover': {
              bgcolor: '#004182'
            },
            '&.Mui-disabled': {
              bgcolor: 'rgba(10, 102, 194, 0.4)'
            }
          }}
          startIcon={!isSubmitting && <MicIcon />}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Create Voice Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 